import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "antd";
import { Share2, ArrowRight } from "lucide-react";
import Navbar from "Components/Navbar/Navbar";
import Footer from "Components/Footer/Footer";
import Reveal from "Components/marketing/Reveal";
import Annotate from "Components/marketing/Annotate";
import RevenueCalculator from "Components/earnings/RevenueCalculator";
import TierLadder from "Components/earnings/TierLadder";
import useCalculatorState from "hooks/useCalculatorState/useCalculatorState";
import { calculateEarnings } from "services/earnings.service";
import {
  renderSuccessNotifications,
  renderErrorNotifications,
} from "helpers/error.helpers";
import { trackEvent, ANALYTICS_EVENTS } from "helpers/analytics.helpers";

function Calculator() {
  const { members, setMembers, bookingRate, setBookingRate } =
    useCalculatorState();

  const result = calculateEarnings({ members, bookingRatePct: bookingRate });

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.CALCULATOR_VIEWED);
  }, []);

  const handleMembersChange = (value) => {
    setMembers(value);
    trackEvent(ANALYTICS_EVENTS.CALCULATOR_ADJUSTED, { input: "members", value });
  };

  const handleRateChange = (value) => {
    setBookingRate(value);
    trackEvent(ANALYTICS_EVENTS.CALCULATOR_ADJUSTED, {
      input: "booking_rate",
      value,
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    trackEvent(ANALYTICS_EVENTS.CALCULATOR_SHARED, { members, rate: bookingRate });

    try {
      await navigator.clipboard.writeText(url);
      renderSuccessNotifications({
        title: "Link copied",
        message: "Send it to a co-organiser — it opens on your numbers.",
      });
    } catch {
      renderErrorNotifications([
        { message: "Couldn't copy the link. Copy it from the address bar." },
      ]);
    }
  };

  const signupHref = `/signup?members=${members}&rate=${bookingRate}`;

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="border-b border-border/40 bg-gradient-shoup">
          <div className="container mx-auto px-4 py-12 md:px-6 md:py-16">
            <Reveal>
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.16em] text-[hsl(var(--brand-coral))]">
                Revenue calculator
              </p>
            </Reveal>
            <Reveal delay={60}>
              <h1 className="text-statement max-w-[16ch] text-4xl text-[hsl(var(--brand-coral))] md:text-6xl lg:text-7xl">
                What could your community earn?
              </h1>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-6 max-w-[52ch] text-lg leading-relaxed text-foreground/70">
                Move the two sliders. This is money{" "}
                <Annotate>on top of</Annotate> whatever you already collect from
                your members.
              </p>
            </Reveal>
          </div>
        </section>

        {/* `estimate` is the deep-link target for every "open the calculator"
            CTA — those promise the sliders, not the hero above them. */}
        <section id="estimate" className="scroll-mt-24 py-14 md:py-20">
          <div className="container mx-auto px-4 md:px-6">
            <RevenueCalculator
              members={members}
              bookingRate={bookingRate}
              onMembersChange={handleMembersChange}
              onBookingRateChange={handleRateChange}
              footer={
                <div className="space-y-3">
                  <Link
                    to={signupHref}
                    className="block"
                    onClick={() =>
                      trackEvent(ANALYTICS_EVENTS.CALCULATOR_CTA_CLICKED, {
                        members,
                        rate: bookingRate,
                      })
                    }
                  >
                    <Button type="primary" size="large" block>
                      Register your community
                    </Button>
                  </Link>
                  <Button
                    size="large"
                    block
                    icon={<Share2 size={15} />}
                    onClick={handleShare}
                  >
                    Copy link to these numbers
                  </Button>
                </div>
              }
            />
          </div>
        </section>

        <section className="border-t border-border/40 py-14 md:py-20">
          <div className="container mx-auto px-4 md:px-6">
            <Reveal>
              <h2 className="text-statement text-2xl text-foreground md:text-3xl">
                How the tiers work
              </h2>
              <p className="mt-3 max-w-[56ch] text-muted-foreground">
                Your tier sets your share of the platform fee and the ceiling on
                your monthly bonus. It&apos;s based on the members you bring onto
                ShoUp, and it&apos;s reviewed as your community grows.
              </p>
            </Reveal>
            <Reveal delay={80}>
              <TierLadder activeTierId={result.tier.id} className="mt-7" />
            </Reveal>
          </div>
        </section>

        <section className="border-t border-border/40 py-14 md:py-20">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <Reveal>
                <h2 className="text-statement max-w-[14ch] text-3xl text-[hsl(var(--brand-coral))] md:text-5xl">
                  Small communities can win big here.
                </h2>
              </Reveal>
              <Reveal delay={90}>
                <div className="space-y-5">
                  <p className="text-lg leading-relaxed text-muted-foreground">
                    A 50 person run club that shows up every week can earn more
                    than a 1,000 member WhatsApp group that doesn&apos;t.
                  </p>
                  <p className="font-display text-2xl font-semibold italic leading-snug text-[hsl(var(--brand-lavender))]">
                    Energy beats size. Consistency beats hype.
                  </p>
                  <Link to={signupHref} className="inline-flex">
                    <Button type="primary" size="large">
                      Register your community <ArrowRight size={15} />
                    </Button>
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default Calculator;
